import { membershipDay, type MembershipSummary, type OwedBalance } from "@teamna/shared";
import { useState } from "react";
import { Button, MoneyIcon, Panel, RadioGroup } from "../../components/index.ts";
import { formatMoney } from "@teamna/web-client/lib/format.ts";
import { RecordRenewalPanel } from "./membership.tsx";
import { OwedPaymentPanel, owedWhen, SellPackagePanel } from "./panels.tsx";

/**
 * Record a payment (#ai-feedback 2026-10-26, card #5): one button and one panel for every cash or
 * bank transfer payment at the desk. It was two: "Sell a package", and "Record a payment" for a
 * package that is owed.
 *
 * When the family owes a package, or the client's membership waits for the next month, the panel
 * first asks what the payment is for: one of those, or a new package. With nothing owed and one
 * person, it goes straight to the new package. With two or more children, "A new package for ..."
 * names each child, because a package is for one child.
 *
 * Each answer opens the panel of that job in place (it does not slide in again), with Back to the
 * question. The panels keep their own rules: the sale, the owed payment and the membership month.
 */

/** A person a new package can be for: the participant id and the name. */
/** `groupId`: the person's group, so a new package offers the packages of that group first. */
export type PaymentPerson = { id: string; displayName: string; groupId?: string | null };

type Target = { kind: "owed"; purchaseId: string } | { kind: "renewal" } | { kind: "new"; participantId: string };

function targetKey(target: Target): string {
  return target.kind === "owed" ? `owed:${target.purchaseId}` : target.kind === "renewal" ? "renewal" : `new:${target.participantId}`;
}

/** The answers to "What is the payment for?", in order: owed packages, the membership month, a new package. */
export function paymentTargets(input: {
  people: PaymentPerson[];
  owed: OwedBalance[];
  renewal: { membership: MembershipSummary } | null;
}): Target[] {
  return [
    ...input.owed.map((row) => ({ kind: "owed" as const, purchaseId: row.purchaseId })),
    ...(input.renewal?.membership.renewal ? [{ kind: "renewal" as const }] : []),
    ...input.people.map((person) => ({ kind: "new" as const, participantId: person.id })),
  ];
}

/**
 * The button that opens the panel, the same in every place. The panel chooses its first step from
 * the profile, so an unread profile is not "nothing owed": the caller disables the button until it is
 * read, and the button reads it again (`refresh`) before it opens, because another person may have
 * changed it since. When that read fails, the panel does not open (the card shows the error).
 */
export function RecordPaymentButton({
  onClick,
  refresh,
  disabled = false,
  variant = "secondary",
  fullWidth = false,
}: {
  onClick: () => void;
  refresh: () => Promise<{ isError: boolean }>;
  disabled?: boolean;
  variant?: "primary" | "secondary";
  fullWidth?: boolean;
}) {
  const [reading, setReading] = useState(false);

  async function open() {
    setReading(true);
    const result = await refresh().finally(() => setReading(false));
    if (!result.isError) onClick();
  }

  return (
    <Button variant={variant} fullWidth={fullWidth} disabled={disabled} loading={reading} onClick={() => void open()}>
      <MoneyIcon size={18} />
      Record a payment
    </Button>
  );
}

export function RecordPaymentPanel({
  businessId,
  people,
  memberName,
  owed,
  renewal = null,
  accountParticipantId,
  timezone,
  onClose,
}: {
  businessId: string;
  /** Who a new package can be for: the child of a card, or each child of the family. */
  people: PaymentPerson[];
  memberName: string;
  /** The family's owed packages. */
  owed: OwedBalance[];
  /** The client's membership with its open month, from that client's profile. */
  renewal?: { participantId: string; name: string; membership: MembershipSummary } | null;
  /** A participant of the family account, for an owed package when no person is listed. */
  accountParticipantId?: string | null;
  timezone: string | undefined;
  onClose: () => void;
}) {
  const targets = paymentTargets({ people, owed, renewal });
  const only = targets.length === 1 ? targets[0]! : null;
  const [chosen, setChosen] = useState<string | null>(targets[0] ? targetKey(targets[0]) : null);
  const [open, setOpen] = useState<Target | null>(only);
  // The question slides in once. The step after it, and the question again after Back, change in place.
  const [stepped, setStepped] = useState(false);
  const asked = only === null;
  const back = asked
    ? () => {
        setOpen(null);
        setStepped(true);
      }
    : undefined;
  // An owed package belongs to the family: any child's id reaches it.
  const anyone = people[0]?.id ?? accountParticipantId ?? renewal?.participantId ?? "";

  if (open?.kind === "new") {
    const person = people.find((row) => row.id === open.participantId);
    if (person) {
      return (
        <SellPackagePanel
          businessId={businessId}
          participantId={person.id}
          groupId={person.groupId ?? null}
          name={person.displayName}
          memberName={memberName}
          onBack={back}
          animate={!asked}
          onClose={onClose}
        />
      );
    }
  }
  if (open?.kind === "owed") {
    const row = owed.find((item) => item.purchaseId === open.purchaseId);
    if (row) {
      return (
        <OwedPaymentPanel
          businessId={businessId}
          participantId={anyone}
          name={memberName}
          memberName={memberName}
          owed={row}
          timezone={timezone}
          onBack={back}
          animate={!asked}
          onClose={onClose}
        />
      );
    }
  }
  if (open?.kind === "renewal" && renewal?.membership.renewal) {
    return (
      <RecordRenewalPanel
        businessId={businessId}
        participantId={renewal.participantId}
        name={renewal.name}
        membership={renewal.membership}
        onBack={back}
        animate={!asked}
        onClose={onClose}
      />
    );
  }

  const options = targets.map((target) => {
    if (target.kind === "owed") {
      const row = owed.find((item) => item.purchaseId === target.purchaseId)!;
      return { value: targetKey(target), label: `${row.packageName}, ${formatMoney(row.amountFils)}`, hint: `Owed. ${owedWhen(row, timezone)}.` };
    }
    if (target.kind === "renewal") {
      const membership = renewal!.membership;
      const month = membership.renewal!;
      return {
        value: targetKey(target),
        label: `${membership.name}, ${formatMoney(month.amountFils)}`,
        hint: `The month from ${membershipDay(month.dueOn)}. ${month.status === "overdue" ? "Late." : "Not paid yet."}`,
      };
    }
    const person = people.find((row) => row.id === target.participantId)!;
    return {
      value: targetKey(target),
      label: people.length > 1 ? `A new package for ${person.displayName}` : "A new package",
      hint: "Choose the package and how they paid.",
    };
  });
  const picked = targets.find((target) => targetKey(target) === chosen) ?? null;

  return (
    <Panel
      open
      onClose={onClose}
      title={`Record a payment for ${memberName}`}
      animate={!stepped}
      actions={
        <>
          <Button disabled={!picked} onClick={() => picked && setOpen(picked)}>
            Continue
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <RadioGroup legend="What is the payment for?" options={options} value={chosen} onChange={setChosen} />
    </Panel>
  );
}
